// ===================== Receipts.jsx =====================
// - Adds a Page Summary at the end (Total Grand, Total Paid, Total Balance)
// - Excludes cancelled receipts from summary math
// - Leaves your backend endpoints and payloads UNCHANGED
// - Everything else (filters, edit, cancel, print) preserved

import React, { useState, useEffect, useMemo } from "react";
import API from "../utils/api";
import bgImage from "../assets/airmax.webp";

const currency = (n) => Number(n || 0).toFixed(2);

const Receipts = () => {
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [allReceipts, setAllReceipts] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    customer_name: "",
    payment_type: "cash",
    items: [], // [{id, product_id, product_name, quantity, unit, unit_price, subtotal}]
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchAllReceipts(); }, []);

  const fetchAllReceipts = async () => {
    try {
      const res = await API.get(`/receipt.php?all=1`);
      setAllReceipts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching receipts", err);
      setAllReceipts([]);
    }
  };

  // live suggestions
  useEffect(() => {
    const t = setTimeout(async () => {
      if (search.trim().length >= 2) {
        try {
          const res = await API.get(`/receipt.php?name=${encodeURIComponent(search.trim())}`);
          setSuggestions(Array.isArray(res.data) ? res.data : []);
        } catch {
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const printServerReceipt = async (saleId) => {
    try {
      const { data } = await API.get(`/receipt.php?sale_id=${saleId}`);
      const receiptHtml = data?.receiptHtml || "";

      const win = window.open("", "", "width=440,height=760");
      win.document.write(`
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Receipt</title>
            <style>
              @media print {
                @page { size: 80mm auto; margin: 0 }
                html, body { width: 80mm; margin: 0 }
                body { zoom: 1.15; }
              }
              html, body {
                width: 80mm; margin: 0; padding: 0; background: #fff; color: #000;
                font-family: "Courier New", ui-monospace, monospace; font-size: 15px; line-height: 1.1;
                -webkit-print-color-adjust: exact; print-color-adjust: exact; font-variant-numeric: tabular-nums;
                -webkit-font-smoothing: none; -moz-osx-font-smoothing: auto;
              }
              .wrap { padding: 8px 8px 12px }
              .inkify, .inkify * { color: #000 !important; font-weight: 900 !important; letter-spacing: 0.1px !important;
                                   -webkit-text-stroke: 0.35px #000; text-shadow: none !important; }
              .r-title { text-align: center; font-size: 20px; margin: 0 0 6px 0; }
              .bar { border-top: 3px solid #000; margin: 6px 0; }
              .bar-thin { border-top: 2px solid #000; margin: 4px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 2px; }
              th, td { padding: 3px 0; border-bottom: 2px solid #000; }
              th { font-size: 13px; }
              .foot { text-align: center; margin-top: 8px; font-size: 13px; }
            </style>
          </head>
          <body onload="window.focus(); window.print();" onafterprint="window.close()">
            <div class="wrap">
              <div class="r-title inkify">Petrah Collection - POS Receipt</div>
              <div class="bar"></div>
              <div class="inkify">${receiptHtml}</div>
              <div class="bar-thin"></div>
              <div class="foot inkify">Thank you!</div>
            </div>
          </body>
        </html>
      `);
      win.document.close();
    } catch (e) {
      console.error("Print error", e);
      alert("Failed to load receipt for printing.");
    }
  };

  // ===== Cancel a receipt (soft cancel handled by backend) =====
  const cancelReceipt = async (saleId) => {
    if (!window.confirm("Cancel this receipt? It will be marked as cancelled and stock will be restored.")) return;
    try {
      setDeleting(true);
      const { data } = await API.post(`/receipt.php?action=cancel`, { sale_id: saleId });
      if (data?.ok) {
        await fetchAllReceipts();
        alert("Receipt cancelled.");
      } else {
        throw new Error(data?.message || "Cancel failed");
      }
    } catch (err) {
      console.error(err);
      alert("Could not cancel receipt.");
    } finally {
      setDeleting(false);
    }
  };

  // ===== Edit modal helpers =====
  const openEdit = async (r) => {
    try {
      const { data } = await API.get(`/receipt.php?sale_id=${r.id}`);
      const rec = data?.data; // { items:[{product_id,...}] }
      if (!rec) throw new Error("Missing receipt data");

      setEditForm({
        id: rec.sale_id,
        customer_name: rec.customer_name || "",
        payment_type: (rec.payment_type || "cash").toLowerCase(),
        items: (rec.items || []).map((it) => ({
          id: it.id, // if present
          product_id: Number(it.product_id),
          product_name: it.product_name,
          quantity: Number(it.quantity || 0),
          unit: it.unit || "pair",
          unit_price: Number(it.unit_price || 0),
          subtotal: Number(
            (it.display_subtotal ?? it.subtotal ?? (Number(it.unit_price || 0) * Number(it.quantity || 0)))
          ),
        })),
      });
      setEditOpen(true);
    } catch (e) {
      console.error(e);
      alert("Failed to load receipt for editing.");
    }
  };

  const editTotals = useMemo(() => {
    const grand = (editForm.items || []).reduce(
      (s, it) => s + Number(Number(it.unit_price) * Number(it.quantity)),
      0
    );
    return { grand_total: grand };
  }, [editForm]);

  const changeItem = (idx, patch) => {
    setEditForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };

  const removeItem = (idx) => {
    setEditForm((prev) => {
      const items = prev.items.filter((_, i) => i !== idx);
      return { ...prev, items };
    });
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      const payload = {
        sale_id: editForm.id,
        customer_name: editForm.customer_name?.trim() || null,
        payment_type: editForm.payment_type,
        items: editForm.items.map((it) => ({
          id: it.id, // may be undefined for new rows
          product_id: Number(it.product_id),
          quantity: Number(it.quantity),
          unit: it.unit || null,
          unit_price: Number(it.unit_price),
        })),
      };
      const { data } = await API.post(`/receipt.php?action=update`, payload);
      if (data?.ok) {
        setEditOpen(false);
        await fetchAllReceipts();
        alert("Receipt updated.");
      } else {
        throw new Error(data?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  // ===== Helpers used both in list + summary (keeps math identical) =====
  const isCancelled = (r) =>
    (typeof r.status === "string" && r.status.toLowerCase() === "cancelled") ||
    Boolean(r.cancelled_at) ||
    Boolean(r.is_cancelled);

  const computeGrandTotal = (r) => {
    if (typeof r.grand_total === "number") return r.grand_total;
    if (!Array.isArray(r.items)) return 0;
    return r.items.reduce((sum, it) => {
      const line = Number(
        (it.display_subtotal ??
          (Number(it.unit_price || 0) * Number(it.quantity || 0)))
      );
      return sum + (Number.isFinite(line) ? line : 0);
    }, 0);
  };

  const computePaid = (r) => (typeof r.paid === "number" ? r.paid : 0);

  const computeBalance = (r, grandTotal, paid) =>
    (typeof r.balance === "number" ? r.balance : Math.max(0, Number(grandTotal) - Number(paid)));

  // ===== Filters (unchanged behavior) =====
  const filteredReceipts = allReceipts
    .filter((r) => {
      const dateOnly = (r.date || "").split(" ")[0] || "";
      const matchesSearch = (r.customer_name || "").toLowerCase().includes(search.toLowerCase());
      const matchesDate = selectedDate ? dateOnly === selectedDate : true;
      const matchesMonth = selectedMonth ? dateOnly.startsWith(selectedMonth) : true;
      return matchesSearch && matchesDate && matchesMonth;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // ===== NEW: Page Summary (excludes cancelled receipts) =====
  const pageSummary = useMemo(() => {
    let count = 0;
    let totalGrand = 0;
    let totalPaid = 0;
    let totalBalance = 0;

    for (const r of filteredReceipts) {
      if (isCancelled(r)) continue; // exclude cancelled from totals
      const grand = computeGrandTotal(r);
      const paid = computePaid(r);
      const bal = computeBalance(r, grand, paid);
      count += 1;
      totalGrand += Number(grand);
      totalPaid += Number(paid);
      totalBalance += Number(bal);
    }

    return {
      count,
      totalGrand,
      totalPaid,
      totalBalance,
    };
  }, [filteredReceipts]); // recalculates whenever filters/data change

  return (
    <div className="relative min-h-screen bg-gray-100">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10 blur-sm"
        style={{ backgroundImage: `url(${bgImage})` }}
      />

      <div className="relative z-10 max-w-5xl mx-auto p-4">
        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow mb-6">
          <h2 className="text-2xl font-bold mb-4 text-center text-gray-700">🧾 Receipt Viewer</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 Customer name"
              className="px-3 py-2 border rounded text-sm"
            />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border rounded text-sm"
            />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border rounded text-sm"
            />
          </div>

          {suggestions.length > 0 && (
            <ul className="bg-white border rounded shadow max-h-40 overflow-auto">
              {suggestions.map((s) => (
                <li
                  key={s.id}
                  className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                  onClick={() => {
                    setSearch(s.customer_name || "");
                    setSuggestions([]);
                  }}
                >
                  {s.customer_name} — {s.date}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Receipts */}
        {filteredReceipts.length === 0 ? (
          <p className="text-center text-sm text-gray-500">No receipts found.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredReceipts.map((r) => {
              const isCredit = (r.payment_type || "").toLowerCase() === "credit";
              const cancelled = isCancelled(r);

              const grandTotal = computeGrandTotal(r);
              const paid = computePaid(r);
              const balance = computeBalance(r, grandTotal, paid);

              return (
                <div
                  key={r.id}
                  className={`relative bg-white border shadow p-4 w-full font-mono text-[12px] rounded-xl ${
                    cancelled ? "opacity-75" : ""
                  }`}
                >
                  {/* CANCELLED watermark */}
                  {cancelled && (
                    <div className="pointer-events-none select-none absolute inset-0 grid place-items-center">
                      <div
                        className="text-red-600 font-extrabold tracking-widest"
                        style={{
                          transform: "rotate(-18deg)",
                          fontSize: 36,
                          opacity: 0.18,
                          border: "3px solid currentColor",
                          padding: "6px 16px",
                          borderRadius: "8px",
                        }}
                      >
                        CANCELLED
                      </div>
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-left">
                      <p className="font-bold text-sm">📄 Petrah Collection - POS Receipt</p>
                      <p className="text-xs text-gray-500">{r.date}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => printServerReceipt(r.id)}
                        className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                        title="Print receipt"
                        disabled={cancelled}
                      >
                        🖨 Print
                      </button>
                      <button
                        onClick={() => openEdit(r)}
                        className="bg-amber-600 text-white text-xs px-3 py-1 rounded hover:bg-amber-700 disabled:opacity-50"
                        title="Edit receipt"
                        disabled={cancelled}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => cancelReceipt(r.id)}
                        className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                        disabled={deleting || cancelled}
                        title="Cancel receipt"
                      >
                        🗑 Cancel
                      </button>
                    </div>
                  </div>
                  <hr className="border-dashed border-t border-gray-300 mb-2" />

                  {/* Customer & Payment */}
                  <p>
                    <strong>Customer:</strong> {r.customer_name || "-"}
                  </p>
                  <p>
                    <strong>Payment Type:</strong>{" "}
                    <span className={isCredit ? "text-red-600 font-semibold" : ""}>
                      {r.payment_type || "N/A"}
                    </span>
                  </p>

                  {/* Items */}
                  <table className="w-full text-left text-xs mt-2 mb-3">
                    <thead>
                      <tr>
                        <th className="px-1">Product</th>
                        <th className="px-1">Qty</th>
                        <th className="px-1">Unit</th>
                        <th className="px-1">Unit Price</th>
                        <th className="px-1">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(r.items || []).map((item, i) => {
                        const line = Number(
                          (item.display_subtotal ??
                            (Number(item.unit_price || 0) * Number(item.quantity || 0)))
                        );
                        return (
                          <tr key={i}>
                            <td className="px-1">
  {item.product_name}
  {(item.size || item.color) && (
    <span className="text-gray-500">
      {" "}
      (
      {[item.size && `size ${item.size}`, item.color]
        .filter(Boolean)
        .join(", ")}
      )
    </span>
  )}
</td>

                            <td className="px-1">{item.quantity}</td>
                            <td className="px-1">{item.unit}</td>
                            <td className="px-1">{Number(item.unit_price || 0).toFixed(2)}</td>
                            <td className="px-1">{(Number.isFinite(line) ? line : 0).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="space-y-1">
                    <p className="font-bold">Grand Total: {currency(grandTotal)} Kesh</p>
                    <p>Amount Paid: {currency(paid)} Kesh</p>
                    <p className={isCredit ? "text-red-700 font-semibold" : ""}>
                      {isCredit ? "Remaining Debt" : "Balance"}: {currency(balance)} Kesh
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===== NEW: Page Summary (at end of page) ===== */}
        <div className="mt-8">
          <div className="bg-white border rounded-xl shadow p-4">
            <h3 className="text-lg font-bold text-gray-700 mb-2">📊 Page Summary (excluding cancelled)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-gray-500">Receipts Count</div>
                <div className="text-xl font-semibold">{pageSummary.count}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-gray-500">Total Grand</div>
                <div className="text-xl font-semibold">{currency(pageSummary.totalGrand)} Kesh</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-gray-500">Total Paid</div>
                <div className="text-xl font-semibold">{currency(pageSummary.totalPaid)} Kesh</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <div className="text-gray-500">Total Balance</div>
                <div className="text-xl font-semibold">{currency(pageSummary.totalBalance)} Kesh</div>
              </div>
            </div>
          </div>
        </div>
        {/* ===== End Summary ===== */}
      </div>

      {/* ===== Edit Modal ===== */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Edit Receipt #{editForm.id}</h3>
              <button onClick={() => setEditOpen(false)} className="text-gray-600 hover:text-black">
                ✖
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold">Customer Name</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={editForm.customer_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, customer_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold">Payment Type</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={editForm.payment_type}
                  onChange={(e) => setEditForm((f) => ({ ...f, payment_type: e.target.value }))}
                >
                  <option value="cash">cash</option>
                  <option value="credit">credit</option>
                </select>
              </div>
            </div>

            <div className="border rounded">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Product ID</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2">Qty</th>
                    <th className="p-2">Unit</th>
                    <th className="p-2">Unit Price</th>
                    <th className="p-2">Subtotal</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {editForm.items.map((it, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2">
                        <input
                          type="number"
                          className="w-24 border rounded px-2 py-1"
                          value={it.product_id}
                          onChange={(e) => changeItem(idx, { product_id: Number(e.target.value) })}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          className="w-full border rounded px-2 py-1"
                          value={it.product_name || ""}
                          onChange={(e) => changeItem(idx, { product_name: e.target.value })}
                          placeholder="optional"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="number"
                          className="w-20 text-center border rounded px-2 py-1"
                          value={it.quantity}
                          min={0}
                          onChange={(e) => changeItem(idx, { quantity: Number(e.target.value) })}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          className="w-20 text-center border rounded px-2 py-1"
                          value={it.unit || "pair"}
                          onChange={(e) => changeItem(idx, { unit: e.target.value })}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="number"
                          step="0.01"
                          className="w-24 text-center border rounded px-2 py-1"
                          value={it.unit_price}
                          onChange={(e) => changeItem(idx, { unit_price: Number(e.target.value) })}
                        />
                      </td>
                      <td className="p-2 text-right">
                        {currency(Number(it.unit_price) * Number(it.quantity))}
                      </td>
                      <td className="p-2 text-center">
                        <button onClick={() => removeItem(idx)} className="text-red-600 hover:underline">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-3">
              <div className="text-sm">
                <button
                  className="px-3 py-1 border rounded mr-2"
                  onClick={() =>
                    setEditForm((f) => ({
                      ...f,
                      items: [
                        ...f.items,
                        { product_id: 0, product_name: "", quantity: 1, unit: "pair", unit_price: 0 },
                      ],
                    }))
                  }
                >
                  + Add line
                </button>
                <span className="ml-2 text-gray-500">
                  Grand Total: <b>{currency(editTotals.grand_total)} Kesh</b>
                </span>
              </div>
              <div className="flex gap-2">
                <button className="px-4 py-2 border rounded" onClick={() => setEditOpen(false)}>
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-emerald-600 text-white rounded disabled:opacity-50"
                  onClick={saveEdit}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;
